import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Trash2, CheckCircle2, Search, Database, BarChart3, Megaphone, Settings2,
} from "lucide-react";
import BasePricelistUploader from "@/components/BasePricelistUploader";
import PricelistChangesPanel from "@/components/PricelistChangesPanel";
import PricelistDiffPanel from "@/components/PricelistDiffPanel";
import PricelistFreshnessBanner from "@/components/PricelistFreshnessBanner";

const fmt = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD" }).format(v)
    : v;

// Debounce helper for search-as-you-type
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const PAGE_SIZE = 50;

const TABS = [
  { id: "overview", label: "Aperçu trimestriel", icon: BarChart3 },
  { id: "announcements", label: "Annonces officielles", icon: Megaphone },
  { id: "search", label: "Rechercher", icon: Search },
  { id: "admin", label: "Uploader la nouvelle Price List", icon: Settings2 },
];

export default function PriceList() {
  const [tab, setTab] = useState("overview");

  // Base price list (bundled Fortinet Q2 2026 catalog)
  const [baseMeta, setBaseMeta] = useState(null);
  const [active, setActive] = useState(null);

  // Search state — only used in the "Rechercher" tab but kept at the page
  // level so the user can come back to their query after switching tabs.
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("");
  const [ptype, setPtype] = useState("");
  const [offset, setOffset] = useState(0);
  const [searchResult, setSearchResult] = useState({ items: [], total: 0 });
  const [searching, setSearching] = useState(false);
  const debouncedQuery = useDebounced(query, 250);

  useEffect(() => {
    api.get("/pricelist/base/meta")
      .then((r) => setBaseMeta(r.data))
      .catch(() => toast.error("Liste de prix de référence indisponible"));
    api.get("/pricelist/active")
      .then((r) => setActive(r.data.active ? r.data : null))
      .catch(() => {});
  }, []);

  useEffect(() => { setOffset(0); }, [debouncedQuery, family, ptype]);

  useEffect(() => {
    if (tab !== "search") return;
    setSearching(true);
    api.get("/pricelist/base/search", {
      params: { q: debouncedQuery, family, ptype, limit: PAGE_SIZE, offset },
    })
      .then((r) => setSearchResult(r.data))
      .catch(() => setSearchResult({ items: [], total: 0 }))
      .finally(() => setSearching(false));
  }, [tab, debouncedQuery, family, ptype, offset]);

  const reloadActive = () =>
    api.get("/pricelist/active").then((r) => setActive(r.data.active ? r.data : null)).catch(() => {});

  const onReset = async () => {
    if (!window.confirm("Désactiver vos surcharges et revenir à la liste officielle ?")) return;
    try {
      await api.delete("/pricelist");
      toast.success("Surcharges désactivées");
      reloadActive();
    } catch {
      toast.error("Échec de la réinitialisation");
    }
  };

  const total = searchResult.total || 0;
  const items = searchResult.items || [];
  const overrideSkus = useMemo(
    () => new Set((active?.rows || []).map((r) => r.sku)),
    [active]
  );
  const overrideMap = useMemo(() => {
    const m = new Map();
    (active?.rows || []).forEach((r) => m.set(r.sku, r));
    return m;
  }, [active]);

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Compact header — title + at-a-glance metadata */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Liste de prix Fortinet</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5 flex-wrap">
            <Database className="h-3 w-3 text-[#D42B1E]" />
            {baseMeta ? (
              <>
                <span className="font-medium text-zinc-700">{baseMeta.row_count.toLocaleString("fr-FR")} références</span>
                <span className="text-zinc-300">·</span>
                <span className="font-mono text-zinc-600 break-all">{baseMeta.filename}</span>
              </>
            ) : (
              <span>Chargement…</span>
            )}
          </p>
        </div>
        {active && (
          <div className="border border-emerald-200 bg-emerald-50/60 rounded-sm px-3 py-2 flex items-center gap-2 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-emerald-900 font-medium">Surcharge active : <span className="font-mono">{active.filename}</span> ({active.row_count})</span>
            <button
              type="button"
              onClick={onReset}
              className="ml-2 text-emerald-800 hover:text-emerald-950 inline-flex items-center gap-1"
              data-testid="pricelist-reset-button"
            >
              <Trash2 className="h-3 w-3" /> retirer
            </button>
          </div>
        )}
      </div>

      {/* Freshness reminder — sticky at the top of every tab so the user
          always sees how recent the loaded pricelist is. */}
      <PricelistFreshnessBanner meta={baseMeta} />

      {/* Tab navigation */}
      <div className="border-b border-zinc-200 mt-6 mb-6 sticky top-14 bg-[#F8F9FA] z-20" data-testid="pricelist-tabs">
        <div className="flex items-stretch gap-1 overflow-x-auto -mb-px">
          {TABS.map((t) => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={[
                  "inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  isActive
                    ? "border-[#D42B1E] text-[#D42B1E]"
                    : "border-transparent text-zinc-500 hover:text-zinc-900 hover:border-zinc-300",
                ].join(" ")}
                data-testid={`pricelist-tab-${t.id}`}
                aria-pressed={isActive}
              >
                <t.icon className={`h-4 w-4 ${isActive ? "text-[#D42B1E]" : "text-zinc-400"}`} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB CONTENT */}
      {tab === "overview" && (
        <div data-testid="pricelist-tab-content-overview">
          <PricelistDiffPanel />
        </div>
      )}

      {tab === "announcements" && (
        <div data-testid="pricelist-tab-content-announcements">
          <PricelistChangesPanel embedded />
        </div>
      )}

      {tab === "admin" && (
        <div data-testid="pricelist-tab-content-admin">
          <BasePricelistUploader
            baseMeta={baseMeta}
            onMetaChange={() => api.get("/pricelist/base/meta").then((r) => setBaseMeta(r.data)).catch(() => {})}
          />
        </div>
      )}

      {tab === "search" && (
        <div data-testid="pricelist-tab-content-search">
          {/* Search bar */}
          <div className="border border-zinc-200 bg-white rounded-sm p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-7 relative">
                <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Rechercher par SKU, nom, description (ex. FG-100F, FortiAnalyzer 200G, FAZ-VM)..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 rounded-sm"
                  data-testid="base-pricelist-search-input"
                  autoFocus
                />
              </div>
              <select
                value={family}
                onChange={(e) => setFamily(e.target.value)}
                className="md:col-span-3 h-10 rounded-sm border border-zinc-300 bg-white px-3 text-sm"
                data-testid="base-pricelist-family-select"
              >
                <option value="">Toutes les familles</option>
                {(baseMeta?.families || []).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <select
                value={ptype}
                onChange={(e) => setPtype(e.target.value)}
                className="md:col-span-2 h-10 rounded-sm border border-zinc-300 bg-white px-3 text-sm"
                data-testid="base-pricelist-type-select"
              >
                <option value="">Tous types</option>
                {(baseMeta?.product_types || []).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-zinc-500">
              <span data-testid="base-pricelist-result-count">
                {searching
                  ? "Recherche…"
                  : `${total.toLocaleString("fr-FR")} résultat${total > 1 ? "s" : ""}`}
                {(query || family || ptype) && total > 0 && (
                  <span className="ml-2 text-zinc-400">
                    · page {Math.floor(offset / PAGE_SIZE) + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                  </span>
                )}
              </span>
              {total > PAGE_SIZE && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="base-pricelist-prev-page">Précédent</Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="base-pricelist-next-page">Suivant</Button>
                </div>
              )}
            </div>
          </div>

          {/* Results table — hidden until a search/filter is active */}
          {(query || family || ptype) ? (
            <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
              <div className="overflow-x-auto max-h-[640px]">
                <table className="w-full text-sm" data-testid="base-pricelist-table">
                  <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Référence (SKU)</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Nom</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Famille</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Type</th>
                      <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Prix MSRP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => {
                      const isOverridden = overrideSkus.has(r.sku);
                      const ovPrice = overrideMap.get(r.sku)?.price;
                      return (
                        <tr key={r.sku} className="border-b border-zinc-100 hover:bg-zinc-50/70 transition-colors" data-testid={`base-pricelist-row-${r.sku}`}>
                          <td className="px-4 py-2.5 font-mono text-xs text-zinc-700 align-top whitespace-nowrap">
                            {r.sku}
                            {isOverridden && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700 text-[10px] font-medium">surcharge</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-zinc-900 align-top max-w-md">
                            <div className="font-medium leading-tight">{r.name}</div>
                            {r.description && r.description !== r.name && (
                              <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{r.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-zinc-600 align-top whitespace-nowrap">{r.family || "—"}</td>
                          <td className="px-4 py-2.5 text-xs text-zinc-600 align-top whitespace-nowrap">
                            <span className="inline-block px-2 py-0.5 rounded-sm bg-zinc-100 font-mono">{r.product_type}</span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-right text-zinc-900 align-top whitespace-nowrap">
                            {isOverridden && ovPrice ? (
                              <>
                                <span className="block text-zinc-400 line-through text-xs">{fmt(r.price)}</span>
                                <span className="block text-green-700 font-semibold">{fmt(ovPrice)}</span>
                              </>
                            ) : (
                              fmt(r.price)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {items.length === 0 && !searching && (
                      <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-zinc-500">Aucun résultat. Essayez un autre mot-clé ou changez de famille.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-zinc-200 bg-white rounded-sm p-10 text-center" data-testid="base-pricelist-empty-state">
              <Search className="h-8 w-8 text-zinc-300 mx-auto" />
              <p className="mt-3 text-sm font-medium text-zinc-700">Tapez votre recherche pour afficher les références</p>
              <p className="mt-1 text-xs text-zinc-500 max-w-md mx-auto">
                La liste complète ({baseMeta?.row_count?.toLocaleString("fr-FR") || "—"} SKUs) reste masquée tant qu'un mot-clé, une famille ou un type de produit n'est pas sélectionné ci-dessus.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
